import React from "react";
import { Truck, ExternalLink, Copy, Check, PackageCheck, PackageOpen, ClipboardCheck, XCircle } from "lucide-react";

const CARRIER_URLS = {
  colissimo: (n) => `https://www.laposte.fr/outils/suivre-vos-envois?code=${n}`,
  chronopost: (n) => `https://www.chronopost.fr/tracking-no-cms/suivi-page?listeNumerosLT=${n}`,
  "mondial relay": (n) => `https://www.mondialrelay.fr/suivi-de-colis/?numeroExpedition=${n}`,
  dpd: (n) => `https://trace.dpd.fr/fr/trace/${n}`,
  ups: (n) => `https://www.ups.com/track?loc=fr_FR&tracknum=${n}`,
  dhl: (n) => `https://www.dhl.com/fr-fr/home/tracking.html?tracking-id=${n}`,
  gls: (n) => `https://gls-group.eu/FR/fr/suivi-colis?match=${n}`,
  fedex: (n) => `https://www.fedex.com/fedextrack/?trknbr=${n}`,
};

export const trackingUrl = (carrier, number) => {
  if (!number) return null;
  const key = (carrier || "").toLowerCase().trim();
  const fn = CARRIER_URLS[key];
  return fn ? fn(encodeURIComponent(number)) : null;
};

const STEPS = [
  { key: "confirmée", label: "Confirmée", icon: ClipboardCheck },
  { key: "en préparation", label: "Préparation", icon: PackageOpen },
  { key: "expédiée", label: "Expédiée", icon: Truck },
  { key: "livrée", label: "Livrée", icon: PackageCheck },
];

export const OrderTimeline = ({ status, accent = "#E01F1F" }) => {
  if (status === "annulée") {
    return <div data-testid="order-timeline-cancelled" className="flex items-center gap-2 text-[13px] text-red-300 bg-red-950/40 border border-red-900/50 rounded-lg px-3 py-2"><XCircle size={15} /> Commande annulée</div>;
  }
  const idx = Math.max(0, STEPS.findIndex((s) => s.key === status));
  return (
    <div data-testid="order-timeline" className="flex items-center w-full">
      {STEPS.map((s, i) => {
        const done = i <= idx;
        const current = i === idx;
        return (
          <React.Fragment key={s.key}>
            <div className="flex flex-col items-center gap-1 min-w-[56px]">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${done ? "text-white border-transparent" : "bg-[#0D0F14] border-[#2A3040] text-slate-600"}`} style={done ? { background: accent, boxShadow: current ? `0 0 16px ${accent}88` : "none" } : undefined}>
                <s.icon size={14} />
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider ${done ? "text-slate-100" : "text-slate-600"}`}>{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className="flex-1 h-0.5 mx-1 mb-4 rounded" style={{ background: i < idx ? accent : "#222735" }} />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const TrackingCard = ({ order, accent = "#E01F1F" }) => {
  const [copied, setCopied] = React.useState(false);
  if (!order.tracking_number) return null;
  const url = trackingUrl(order.carrier, order.tracking_number);
  const copy = async () => {
    try { await navigator.clipboard.writeText(order.tracking_number); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
  };
  return (
    <div data-testid={`tracking-card-${order.id}`} className="flex flex-wrap items-center justify-between gap-3 bg-[#08090C] border border-[#222735] rounded-xl px-4 py-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}22`, color: accent }}><Truck size={20} /></div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Suivi colis · {order.carrier || "Transporteur"}</div>
          <div className="flex items-center gap-2">
            <span data-testid={`tracking-number-${order.id}`} className="font-mono text-slate-50 font-semibold tracking-wide truncate">{order.tracking_number}</span>
            <button onClick={copy} title="Copier" data-testid={`tracking-copy-${order.id}`} className="text-slate-500 hover:text-white transition-colors">{copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}</button>
          </div>
        </div>
      </div>
      {url ? (
        <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`tracking-link-${order.id}`} className="inline-flex items-center gap-1.5 text-[13px] font-bold text-white rounded-lg px-3.5 py-2 transition-opacity hover:opacity-90" style={{ background: accent }}>
          Suivre mon colis <ExternalLink size={14} />
        </a>
      ) : (
        <span className="text-[12px] text-slate-500">Suivi disponible sur le site du transporteur</span>
      )}
    </div>
  );
};
